import type { MfaEnrollMethod } from '@interfaces/IMfa';

/**
 * Line icons for the second-factor methods, drawn inline rather than pulled from an icon
 * package: they inherit `currentColor`, so one tile style tints them (grey while a method is
 * merely available, green once enrolled) with no per-icon variants.
 *
 * The geometry matches the Trusstor app's security page — the two products share a design
 * system, and a factor should not look like a different thing in each one.
 */

const BASE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

/** Authenticator app — a handset showing a rotating code. */
function AuthenticatorIcon() {
  return (
    <svg {...BASE}>
      <rect x="6" y="2.5" width="12" height="19" rx="2.4" />
      <line x1="9.5" y1="18.6" x2="14.5" y2="18.6" />
      <circle cx="9.2" cy="9" r="1" />
      <circle cx="12" cy="9" r="1" />
      <circle cx="14.8" cy="9" r="1" />
      <line x1="8.4" y1="12.4" x2="15.6" y2="12.4" />
    </svg>
  );
}

/** Email — an envelope. */
function EmailIcon() {
  return (
    <svg {...BASE}>
      <rect x="3" y="5" width="18" height="14" rx="2.4" />
      <path d="M3.6 7.2 11 12.2a1.7 1.7 0 0 0 2 0l7.4-5" />
    </svg>
  );
}

/** SMS / LINE and any future chat-delivered code — a message bubble. */
function MessageIcon() {
  return (
    <svg {...BASE}>
      <path d="M4 5h15a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H9l-4 3.5V6a1 1 0 0 1 1-1z" />
    </svg>
  );
}

/** Backup codes — a key, for the recovery block. */
export function BackupCodesIcon() {
  return (
    <svg {...BASE}>
      <circle cx="8" cy="8" r="4.2" />
      <path d="M11 11l7.4 7.4" />
      <path d="M16.6 16.4l1.8 1.8" />
      <path d="M18.9 14.1l1.8 1.8" />
    </svg>
  );
}

/** The tick inside the "enrolled" tag. Heavier stroke — it renders very small. */
export function EnrolledCheckIcon() {
  return (
    <svg {...BASE} strokeWidth={2}>
      <path d="M5 12.5l4.2 4.2L19 7" />
    </svg>
  );
}

const METHOD_ICONS: Record<MfaEnrollMethod, () => React.JSX.Element> = {
  totp: AuthenticatorIcon,
  email: EmailIcon,
  sms: MessageIcon,
  line: MessageIcon,
};

interface IMfaMethodIconProps {
  method: MfaEnrollMethod;
}

/**
 * The icon for a method. Falls back to the message bubble so a channel the BFF offers but this
 * build has no artwork for still renders a tile rather than a hole.
 */
export function MfaMethodIcon(props: IMfaMethodIconProps) {
  const { method } = props;
  const Icon = METHOD_ICONS[method] ?? MessageIcon;
  return <Icon />;
}
